<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  importItem: { type: Object, default: null },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const form = ref({
  name: '',
  spreadsheet_id: '',
  sheet_name: '',
  api_url: '',
  api_method: 'POST',
  api_headers: '{}',
  payload_template: '{}',
  dedup_key_column: '',
  last_processed_row: 0,
  sync_interval: null,
});

const isEditing = ref(false);

watch(
  () => props.show,
  val => {
    if (val && props.importItem) {
      isEditing.value = true;
      form.value = {
        name: props.importItem.name || '',
        spreadsheet_id: props.importItem.spreadsheet_id || '',
        sheet_name: props.importItem.sheet_name || '',
        api_url: props.importItem.api_url || '',
        api_method: props.importItem.api_method || 'POST',
        api_headers: JSON.stringify(props.importItem.api_headers || {}, null, 2),
        payload_template: JSON.stringify(
          props.importItem.payload_template || {},
          null,
          2
        ),
        dedup_key_column: props.importItem.dedup_key_column || '',
        last_processed_row: props.importItem.last_processed_row || 0,
        sync_interval: props.importItem.sync_interval || null,
      };
    } else if (val) {
      isEditing.value = false;
      form.value = {
        name: '',
        spreadsheet_id: '',
        sheet_name: '',
        api_url: '',
        api_method: 'POST',
        api_headers: '{}',
        payload_template: '{}',
        dedup_key_column: '',
        last_processed_row: 0,
        sync_interval: null,
      };
    }
  }
);

const handleSubmit = () => {
  let parsedHeaders = {};
  let parsedTemplate = {};

  try {
    parsedHeaders = JSON.parse(form.value.api_headers);
  } catch {
    parsedHeaders = {};
  }
  try {
    parsedTemplate = JSON.parse(form.value.payload_template);
  } catch {
    parsedTemplate = {};
  }

  const payload = {
    google_sheet_import: {
      name: form.value.name,
      spreadsheet_id: form.value.spreadsheet_id,
      sheet_name: form.value.sheet_name,
      api_url: form.value.api_url,
      api_method: form.value.api_method,
      api_headers: parsedHeaders,
      payload_template: parsedTemplate,
      dedup_key_column: form.value.dedup_key_column,
      last_processed_row: form.value.last_processed_row,
      sync_interval: form.value.sync_interval ? parseInt(form.value.sync_interval, 10) : null,
    },
  };

  if (isEditing.value && props.importItem) {
    payload.id = props.importItem.id;
  }

  emit('save', payload);
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-5"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-solid-1 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-n-weak"
      >
        <div class="flex items-center justify-between p-6 border-b border-n-weak">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{
              isEditing
                ? t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.EDIT_TITLE')
                : t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.CREATE_TITLE')
            }}
          </h3>
          <button
            class="text-n-slate-11 hover:text-n-slate-12 text-lg"
            @click="emit('close')"
          >
            ✕
          </button>
        </div>

        <form class="p-6 flex flex-col gap-4" @submit.prevent="handleSubmit">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.NAME') }}
            </label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="Ej: Leads TikTok"
            />
          </div>

          <!-- Spreadsheet ID -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              Spreadsheet ID
            </label>
            <input
              v-model="form.spreadsheet_id"
              type="text"
              required
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
            />
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_CONTACTS.SPREADSHEET_HELP') }}
            </p>
          </div>

          <!-- Sheet Name -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.SHEET_NAME') }}
            </label>
            <input
              v-model="form.sheet_name"
              type="text"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="Hoja 1"
            />
          </div>

          <!-- API URL -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              API URL
            </label>
            <input
              v-model="form.api_url"
              type="url"
              required
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
              placeholder="https://api.example.com/webhook"
            />
          </div>

          <!-- API Method -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.API_METHOD') }}
            </label>
            <select
              v-model="form.api_method"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm"
            >
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
            </select>
          </div>

          <!-- API Headers (JSON) -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.API_HEADERS') }}
            </label>
            <textarea
              v-model="form.api_headers"
              rows="3"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm font-mono focus:border-n-brand focus:ring-n-brand"
              placeholder='{ "Authorization": "Bearer xxx" }'
            />
          </div>

          <!-- Payload Template (JSON) -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.PAYLOAD_TEMPLATE') }}
            </label>
            <textarea
              v-model="form.payload_template"
              rows="8"
              class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm font-mono focus:border-n-brand focus:ring-n-brand"
              placeholder='{ "field": "value" }'
            />
            <p class="text-xs text-n-slate-10 mt-1">
              {{ t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.TEMPLATE_HELP') }}
            </p>
          </div>

          <!-- Advanced Settings -->
          <div class="border-t border-n-weak pt-4 mt-2">
             <h4 class="text-sm font-semibold text-n-slate-12 mb-3">Configuración Avanzada (Opcional)</h4>
             
             <div class="grid grid-cols-2 gap-4">
                <!-- Dedup Key Column -->
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    Columna ID Único (Anti-duplicados)
                  </label>
                  <input
                    v-model="form.dedup_key_column"
                    type="text"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
                    placeholder="Ej: Email o Lead ID"
                  />
                  <p class="text-xs text-n-slate-10 mt-1">
                     Si se indica, no se importará si este valor ya existe en el historial.
                  </p>
                </div>

                <!-- Last Processed Row -->
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    Última Fila Procesada
                  </label>
                  <input
                    v-model="form.last_processed_row"
                    type="number"
                    class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:border-n-brand focus:ring-n-brand"
                  />
                  <p class="text-xs text-n-slate-10 mt-1">
                     Edita para reiniciar (0) o saltar filas.
                  </p>
                </div>
             </div>

             <!-- Sync Interval -->
             <div class="mt-4">
               <label class="block text-sm font-medium text-n-slate-12 mb-1">
                 Sincronización Automática
               </label>
               <select
                 v-model="form.sync_interval"
                 class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm"
               >
                 <option :value="null">Manual (sin auto-sync)</option>
                 <option :value="5">Cada 5 minutos</option>
                 <option :value="10">Cada 10 minutos</option>
                 <option :value="15">Cada 15 minutos</option>
                 <option :value="30">Cada 30 minutos</option>
                 <option :value="60">Cada 1 hora</option>
                 <option :value="360">Cada 6 horas</option>
                 <option :value="720">Cada 12 horas</option>
                 <option :value="1440">Cada 24 horas</option>
               </select>
               <p class="text-xs text-n-slate-10 mt-1">
                 Si se configura, el sistema revisará automáticamente la hoja y enviará nuevas filas.
               </p>
             </div>
          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-2 pt-2 border-t border-n-weak">
            <Button
              faded
              slate
              :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.CANCEL')"
              @click="emit('close')"
            />
            <Button
              blue
              :label="
                isEditing
                  ? t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.UPDATE')
                  : t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.MODAL.CREATE')
              "
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
